import React, { useState } from 'react';
import { X, Calendar, Clock, MapPin, Package, CreditCard, Banknote } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';
import { toast } from './ui/sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingModal = ({ isOpen, onClose, preselectedPackage = null }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    package_id: preselectedPackage || '',
    service_date: '',
    service_time: '',
    notes: '',
    payment_method: 'now'
  });

  const packages = {
    mini: { name: 'Mini Load', price: 95 },
    small: { name: 'Small Load (1/4 truck)', price: 175 },
    medium: { name: 'Medium Load (1/2 truck)', price: 325 },
    large: { name: 'Large Load (3/4 truck)', price: 450 },
    full: { name: 'Full Load (14 yards)', price: 600 }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validation
    if (!formData.name || !formData.email || !formData.phone || !formData.address || !formData.package_id || !formData.service_date) {
      toast.error('Missing Information', {
        description: 'Please fill in all required fields'
      });
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/booking/create`, formData);

      if (formData.payment_method === 'now' && response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        // Booking confirmed, payment later
        toast.success('Booking Confirmed!', {
          description: response.data.message
        });
        onClose();
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          package_id: '',
          service_date: '',
          service_time: '',
          notes: '',
          payment_method: 'now'
        });
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error('Booking Failed', {
        description: error.response?.data?.detail || 'There was an error creating your booking. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const selectedPackage = formData.package_id ? packages[formData.package_id] : null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="bg-white rounded-xl shadow-2xl max-w-2xl w-full my-8">
        <div className="sticky top-0 bg-white border-b border-gray-200 rounded-t-xl z-10">
          <div className="flex items-center justify-between p-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Book Your Service</h2>
              <p className="text-gray-600 text-sm mt-1">Fill in the details below to schedule your junk removal</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              Contact Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name" className="text-gray-900 font-semibold mb-2 block">
                  Full Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-gray-900 font-semibold mb-2 block">
                  Phone Number <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="(365) 336-3364"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-gray-900 font-semibold mb-2 block">
                Email Address <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="john@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
              />
            </div>

            <div>
              <Label htmlFor="address" className="text-gray-900 font-semibold mb-2 block">
                <MapPin className="w-4 h-4 inline mr-1" />
                Service Address <span className="text-red-500">*</span>
              </Label>
              <Input
                id="address"
                name="address"
                type="text"
                placeholder="123 Main St, Hamilton, ON"
                value={formData.address}
                onChange={handleChange}
                required
                className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
              />
            </div>
          </div>

          {/* Service Details */}
          <div className="space-y-4 border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900">Service Details</h3>
            
            <div>
              <Label htmlFor="package_id" className="text-gray-900 font-semibold mb-2 block">
                <Package className="w-4 h-4 inline mr-1" />
                Service Package <span className="text-red-500">*</span>
              </Label>
              <Select value={formData.package_id} onValueChange={(value) => handleSelectChange('package_id', value)}>
                <SelectTrigger id="package_id" className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500 h-11">
                  <SelectValue placeholder="Select package" />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={5} className="z-[9999] bg-white">
                  <SelectItem value="mini">Mini Load - $95 + HST</SelectItem>
                  <SelectItem value="small">Small Load (1/4 truck) - $175 + HST</SelectItem>
                  <SelectItem value="medium">Medium Load (1/2 truck) - $325 + HST</SelectItem>
                  <SelectItem value="large">Large Load (3/4 truck) - $450 + HST</SelectItem>
                  <SelectItem value="full">Full Load (14 yards) - $600+ HST</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="service_date" className="text-gray-900 font-semibold mb-2 block">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Service Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="service_date"
                  name="service_date"
                  type="date"
                  value={formData.service_date}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                  required
                  className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                />
              </div>

              <div>
                <Label htmlFor="service_time" className="text-gray-900 font-semibold mb-2 block">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Preferred Time (Optional)
                </Label>
                <Select value={formData.service_time} onValueChange={(value) => handleSelectChange('service_time', value)}>
                  <SelectTrigger className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500 h-11">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5} className="z-[9999] bg-white">
                    <SelectItem value="morning">Morning (8am - 12pm)</SelectItem>
                    <SelectItem value="afternoon">Afternoon (12pm - 4pm)</SelectItem>
                    <SelectItem value="evening">Evening (4pm - 7pm)</SelectItem>
                    <SelectItem value="flexible">Flexible / Anytime</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="notes" className="text-gray-900 font-semibold mb-2 block">
                Additional Notes (Optional)
              </Label>
              <Textarea
                id="notes"
                name="notes"
                placeholder="Any special instructions or items to mention..."
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500 resize-none"
              />
            </div>
          </div>

          {/* Payment Method */}
          <div className="space-y-4 border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900">Payment Method</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => handleSelectChange('payment_method', 'now')}
                className={`p-4 border-2 rounded-lg transition-all ${
                  formData.payment_method === 'now'
                    ? 'border-emerald-600 bg-emerald-50'
                    : 'border-gray-200 hover:border-emerald-300'
                }`}
              >
                <div className="flex items-center justify-center mb-2">
                  <CreditCard className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="text-center">
                  <p className="font-semibold text-gray-900">Pay Now</p>
                  <p className="text-sm text-gray-600">Secure payment via Stripe</p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleSelectChange('payment_method', 'later')}
                className={`p-4 border-2 rounded-lg transition-all ${
                  formData.payment_method === 'later'
                    ? 'border-emerald-600 bg-emerald-50'
                    : 'border-gray-200 hover:border-emerald-300'
                }`}
              >
                <div className="flex items-center justify-center mb-2">
                  <Banknote className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="text-center">
                  <p className="font-semibold text-gray-900">Pay on Service Day</p>
                  <p className="text-sm text-gray-600">Cash or card accepted</p>
                </div>
              </button>
            </div>

            {/* 15% Discount Notice */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <p className="text-emerald-800 text-sm font-semibold">
                💡 Reminder: Get 15% off when all junk is outside and ready for removal!
              </p>
            </div>
          </div>

          {/* Price Summary */}
          {selectedPackage && (
            <div className="bg-gray-50 rounded-lg p-4 border-t">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-gray-600 text-sm">Selected Package</p>
                  <p className="text-gray-900 font-semibold">{selectedPackage.name}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-emerald-600">${selectedPackage.price}</p>
                  <p className="text-gray-600 text-xs">Base price</p>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex gap-4 sticky bottom-0 bg-white pt-4 pb-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-6 font-semibold"
              disabled={loading}
            >
              {loading ? 'Processing...' : formData.payment_method === 'now' ? 'Proceed to Payment' : 'Confirm Booking'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default BookingModal;
